import { find, findIndex } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { groupsApi, merchantsApi, shopsApi, usersApi } from 'api';
import {
  CommissionInfo,
  CopyTextId,
  CrudPage,
  CrudTableAction,
  CrudTableActionType,
  DataGridColumnDefinition,
  ExternalLink,
  SidebarLayout,
  SidebarSize,
  dataGridColumns,
  IntervalCommissionsInfoDialog,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, QueryKey, ShopStatus } from 'enums';
import {
  useCurrencies,
  useMutation,
  usePartialQuery,
  useShopsQuery,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  IntervalCommission,
  Shop,
  ShopsFilters,
} from 'types';
import { formUtils, shopUtils } from 'utils';

import { ShopDetails } from '../ShopDetails';
import { ShopStatusLabel } from '../ShopStatusLabel';

const queryKey = QueryKey.ShopsPaginated;

type Props = {
  title?: string;
  archived?: boolean;
};

export const ShopList: React.FC<Props> = ({ title, archived }) => {
  const navigate = useNavigate();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.shops_list.table.columns',
  });
  const { t: tRemoveDialog } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.shops_list.remove_dialog',
  });

  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.shops_list.table.filters',
  });

  const { getCurrenciesCodes, getFiatCurrencyCode, fiatCurrenciesOptions } =
    useCurrencies();

  const {
    role,
    apiRole,
    isAdmin,
    isTechOperator,
    isMerchant,
    isMerchantOperator,
    isAdminOrTechOperator,
  } = useUser();

  const queryClient = useQueryClient();
  const queryFn = useMemo(() => {
    if (archived) {
      return shopsApi.getAllArchivePaginatedAsRole(apiRole);
    }

    return shopsApi.getAllNotArchivePaginatedAsRole(apiRole);
  }, [archived, apiRole]);

  const queryResult = usePartialQuery(queryKey, queryFn);
  const shops = useMemo(
    () => queryResult.data?.items || [],
    [queryResult.data?.items],
  );

  const usersQuery = useQuery(QueryKey.Users, usersApi.getAll, {
    enabled: isAdminOrTechOperator,
  });

  const { mutate: archive } = useMutation(shopsApi.archive, {
    onSuccess: (data) => {
      if (data.id === selectedShop?.id) {
        setSelectedShop(null);
      }
      queryClient.invalidateQueries(queryKey);
    },
    notifierType: 'remove',
  });

  const { mutate: restore } = useMutation(shopsApi.restore, {
    onSuccess: (data) => {
      if (data.id === selectedShop?.id) {
        setSelectedShop(null);
      }
      queryClient.invalidateQueries(queryKey);
    },
    notifierType: 'execute',
  });

  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const handleOpenShop = useCallback((shop: Shop) => setSelectedShop(shop), []);
  const handleCloseShop = useCallback(() => setSelectedShop(null), []);

  const shopDetailsPage = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SHOP_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SHOP_DETAILS;
    } else if (isMerchant || isMerchantOperator) {
      return ROUTE_PATH.MERCHANT.SHOP_DETAILS;
    }
  }, [isAdmin, isMerchant, isMerchantOperator, isTechOperator]);

  const handleCreateOrEdit = useCallback(
    (id: string) => navigate(generatePath(shopDetailsPage!, { id })),
    [shopDetailsPage, navigate],
  );

  const [
    intervalCommissionsInfoDialogProps,
    setIntervalCommissionsInfoDialogOpen,
  ] = useState<{
    open: boolean;
    intervalCommissions?: IntervalCommission[];
  }>({ open: false });

  const notArchivedActions = useMemo(
    (): CrudTableAction<Shop>[] => [
      {
        type: CrudTableActionType.Details,
        path: shopDetailsPage,
      },
      ...(isMerchantOperator
        ? []
        : [
            {
              type: CrudTableActionType.Remove as const,
              renderDialogContent: (item: Shop) => (
                <div>
                  {tRemoveDialog('confirm')}
                  <div>{item.name}</div>
                </div>
              ),
              onRemove: (item: Shop, { close }: { close: () => void }) =>
                archive(item.id, {
                  onSuccess: close,
                }),
            },
          ]),
    ],
    [archive, shopDetailsPage, isMerchantOperator, tRemoveDialog],
  );

  const archivedActions = useMemo(
    (): CrudTableAction<Shop>[] => [
      {
        type: CrudTableActionType.Restore,
        onRestore: (item) => {
          restore(item.id);
        },
      },
    ],
    [restore],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<Shop>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('name'),
        valueGetter: (item) => item.name || '',
      },
      {
        header: t('api_key'),
        valueGetter: (item) =>
          item.secrets?.apiKey ? <CopyTextId id={item.secrets.apiKey} /> : '',
      },
      {
        header: t('website'),
        valueGetter: (item) => (
          <Fragment>
            <ExternalLink href={item.webSiteUrl} />
            {item.webSiteIp && <div>{item.webSiteIp}</div>}
          </Fragment>
        ),
      },
      {
        header: t('currencies'),
        valueGetter: (item) => getCurrenciesCodes(item),
      },
      {
        header: t('merchant_name'),
        valueGetter: (item) => item.user?.name || '',
        hidden: !isAdminOrTechOperator,
      },
      {
        header: t('commission'),
        valueGetter: (item) => (
          <CommissionInfo
            commission={item.commission}
            payoutCommission={item.payoutCommission}
            intervalCommissions={item.intervalCommissions}
            onIntervalCommissionsButtonClick={(e: React.SyntheticEvent) => {
              e.stopPropagation();
              setIntervalCommissionsInfoDialogOpen({
                open: true,
                intervalCommissions: item.intervalCommissions,
              });
            }}
          />
        ),
      },
      {
        header: t('status'),
        valueGetter: (item) => <ShopStatusLabel status={item.status} />,
      },
      {
        header: t('groups'),
        multiValueRenderer: {
          itemsGetter: (item) => item.groups,
          valueGetter: (group) => <div key={group.id}>{group?.name}</div>,
        },
        hidden: !isAdminOrTechOperator,
      },
    ],
    [getCurrenciesCodes, isAdminOrTechOperator, t],
  );

  const queryShopsFn = useMemo(() => {
    if (archived) {
      return shopsApi.getAllArchiveAsRole(apiRole);
    }

    return shopsApi.getAllNotArchiveAsRole(apiRole);
  }, [archived, apiRole]);
  const queryResultShops = useShopsQuery(QueryKey.Shops, queryShopsFn);

  const { data: merchants } = useQuery(
    QueryKey.Merchants,
    merchantsApi.getAllAsRole(role),
    { enabled: isAdminOrTechOperator },
  );

  const { data: groups = [] } = useQuery(QueryKey.Groups, groupsApi.getAll, {
    enabled: isAdminOrTechOperator,
  });

  const filtersDefinitions: FilterDefinition<ShopsFilters>[] = useMemo(
    () => [
      {
        label: tFilters('name'),
        name: 'id',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: tFilters('status'),
        name: 'status',
        type: FilterDefinitionType.Enum,
        enum: ShopStatus,
        getDisplayName: shopUtils.getShopStatusLabel,
        hidden: archived,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: tFilters('group'),
        name: 'groupId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(groups),
        getDisplayName: (groupId: string) =>
          find(groups, { id: groupId })?.name,
        hidden: !isAdminOrTechOperator,
      },
      {
        label: tFilters('merchant'),
        name: 'merchantId',
        type: FilterDefinitionType.Merchant,
        merchants,
        hidden: !isAdminOrTechOperator,
      },
    ],
    [
      archived,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      groups,
      isAdminOrTechOperator,
      merchants,
      queryResultShops.data,
      tFilters,
    ],
  );

  return (
    <Fragment>
      <SidebarLayout
        sidebarProps={{
          size: SidebarSize.md,
          open: !!selectedShop,
          onClose: handleCloseShop,
          title: selectedShop && (
            <Fragment>
              {selectedShop.name}
              <ShopStatusLabel sx={{ ml: 1 }} status={selectedShop.status} />
            </Fragment>
          ),
          content: (
            <ShopDetails
              shop={selectedShop}
              users={usersQuery.data}
              adminView={isAdminOrTechOperator}
              onIntervalCommissionsButtonClick={() => {
                setIntervalCommissionsInfoDialogOpen({
                  open: true,
                  intervalCommissions: selectedShop?.intervalCommissions,
                });
              }}
            />
          ),
        }}
      >
        <CrudPage
          header={{
            hide: archived,
            title,
            rightContentButton: isMerchantOperator
              ? undefined
              : {
                  onClick: () => handleCreateOrEdit(NEW_ID),
                },
          }}
          filters={{ filtersDefinitions }}
          table={{
            queryResult,
            paginated: true,
            columns,
            onRowClick: handleOpenShop,
            selectedRowIndex: findIndex(shops, {
              id: selectedShop?.id,
            }),
            actions: archived ? archivedActions : notArchivedActions,
          }}
        />
      </SidebarLayout>
      <IntervalCommissionsInfoDialog
        {...intervalCommissionsInfoDialogProps}
        onClose={() => setIntervalCommissionsInfoDialogOpen({ open: false })}
      />
    </Fragment>
  );
};
